import { Controller } from "@hotwired/stimulus";

// GO4TEACH lot 17 (prototype `session`): « Ce que vous étiez en train d'écrire est gardé ».
// What the teacher types in a text area is kept in this browser, per account and per field: a
// session that ended during the writing loses nothing — once signed back in, the page offers the
// draft again (« Brouillon retrouvé », « Effacer »).
//
// ⚠️ The forms post whole pages: the browser cannot tell a save from a session that ended. So a
// draft remembers the text the page held when the writing began (`base`), and when the page comes
// back it is dropped if the page now holds the draft (saved — compared trimmed, as the server
// saves) or a text that changed since (saved, or changed elsewhere: never written over). Written
// at every keystroke — a sending right after the last one keeps it whole.
const PREFIX = "g4t-draft:";
const same = (a, b) => (a ?? "").trim() === (b ?? "").trim();

export default class extends Controller {
  static values = { key: String, owner: String, found: String, clear: String };

  connect() {
    // Turbo's cached copy of a page (a preview, the browser's Back) carries the typed text: it
    // decides nothing — the page from the server does.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;
    this.storageKey = `${PREFIX}${this.ownerValue}:${this.keyValue}`;
    // The server's text (a restored copy's typed text is not it).
    this.base = this.element.defaultValue;
    this.cancelButton()?.addEventListener("click", this.cancel);
    const kept = this.read();
    if (!kept) return;
    if (same(kept.text, this.base) || !same(kept.base, this.base)) {
      this.forget();
      return;
    }
    this.element.value = kept.text;
    this.reveal();
    this.announce();
  }

  disconnect() {
    this.cancelButton()?.removeEventListener("click", this.cancel);
  }

  keep() {
    if (!this.storageKey) return;
    if (same(this.element.value, this.base)) this.forget();
    else this.write({ text: this.element.value, base: this.base });
  }

  // « Annuler » in the section editor: the writing is given up.
  cancel = () => {
    this.forget();
    this.element.value = this.base;
    this.note?.remove();
  };

  cancelButton() {
    return this.element.closest("form")?.querySelector("[data-action~='ds-collapse#toggle']");
  }

  // A hidden form (the section editor) opens to show what was kept.
  reveal() {
    const form = this.element.closest("form");
    if (form?.hidden) {
      form.hidden = false;
      const shown = form.parentElement?.querySelector("[data-ds-collapse-target='content']:not(form)");
      if (shown) shown.hidden = true;
    }
  }

  announce() {
    const note = document.createElement("p");
    note.className = "ds-text-meta";
    note.dataset.testid = "teach-draft-found";
    note.style.cssText = "margin: 0 0 6px; font-size: 12px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap;";
    note.textContent = this.foundValue;
    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "ds-btn ds-btn--ghost ds-btn--sm";
    clear.textContent = this.clearValue;
    clear.addEventListener("click", this.cancel);
    note.appendChild(clear);
    this.element.before(note);
    this.note = note;
  }

  read() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  write(draft) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(draft));
    } catch (_error) {
      // Storage full or refused: the writing goes on, only unkept.
    }
  }

  forget() {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (_error) {
      // Nothing kept.
    }
  }
}

// A deliberate sign-out, or « Utiliser un autre compte »: every draft of this browser goes — a
// shared computer keeps no one's lesson logs.
export class G4tDraftsPurgeController extends Controller {
  purge() {
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith(PREFIX))
        .forEach((key) => window.localStorage.removeItem(key));
    } catch (_error) {
      // Nothing to purge.
    }
  }
}
